import { useState } from "react";

export interface BarPoint {
  label: string;
  value: number;
}

interface VolumeBarChartProps {
  data: BarPoint[];
  height?: number;
}

const W = 900;
const H = 300;
const PAD_L = 40;
const PAD_R = 16;
const PAD_T = 18;
const PAD_B = 30;

export default function VolumeBarChart({ data, height = H }: VolumeBarChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  const n = data.length;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const max = Math.max(1, ...data.map((d) => d.value));
  const yTicks = 5;
  const slot = innerW / n;
  const barW = Math.min(46, slot * 0.55);
  const barX = (i: number) => PAD_L + i * slot + (slot - barW) / 2;
  const yFor = (v: number) => PAD_T + innerH - (v / max) * innerH;
  const bottom = PAD_T + innerH;
  const labelStep = Math.max(1, Math.ceil(n / 6));

  const gridlines = Array.from({ length: yTicks }, (_, i) => {
    const ratio = i / (yTicks - 1);
    return { y: PAD_T + innerH - ratio * innerH, val: Math.round(ratio * max) };
  });

  const hoverP = hover != null ? data[hover] : null;
  const hoverLeft = hover != null ? ((barX(hover) + barW / 2) / W) * 100 : 0;
  const hoverTop = hoverP ? (yFor(hoverP.value) / H) * 100 : 0;

  return (
    <div
      className="relative w-full"
      style={{ height }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        const px = ratio * W;
        if (px < PAD_L) {
          setHover(null);
          return;
        }
        const idx = Math.min(n - 1, Math.floor((px - PAD_L) / slot));
        setHover(idx);
      }}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        role="img"
        aria-label="Mandate volume by week bar chart"
      >
        {gridlines.map((g) => (
          <g key={g.y}>
            <line x1={PAD_L} y1={g.y} x2={W - PAD_R} y2={g.y} stroke="#F1F5F9" strokeWidth="1" />
            <text x={PAD_L - 8} y={g.y + 4} textAnchor="end" className="fill-slate-400" fontSize="11">
              {g.val}
            </text>
          </g>
        ))}

        {data.map((d, i) => {
          const x = barX(i);
          const y = yFor(d.value);
          const h = bottom - y;
          const hovered = hover === i;
          return (
            <g key={d.label}>
              <path
                d={`M${x},${y + 4} a4,4 0 0 1 4,-4 h${barW - 8} a4,4 0 0 1 4,4 V${bottom} H${x} Z`}
                fill={hovered ? "#1E3A8A" : "#1E40AF"}
              />
              {(i % labelStep === 0 || i === n - 1) && (
                <text
                  x={x + barW / 2}
                  y={H - 10}
                  textAnchor="middle"
                  className="fill-slate-400"
                  fontSize="11"
                >
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {hoverP && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-line bg-ink px-3 py-2 shadow-dropdown"
          style={{ left: `${hoverLeft}%`, top: `${hoverTop}%` }}
        >
          <p className="text-[11px] text-slate-300">{hoverP.label}</p>
          <p className="text-[13px] font-semibold text-white">{hoverP.value} created</p>
        </div>
      )}
    </div>
  );
}